import { observer } from 'mobx-react-lite'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { BackHandler, Image, Platform, StyleSheet, TouchableOpacity } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'
import Animated, { FadeIn } from 'react-native-reanimated'
import { StatusBar, StatusBarStyle } from 'expo-status-bar'
import { useIsFocused } from '@react-navigation/native'

import { useFocusEffect, useRouter } from 'expo-router'

import ArrowLeft from '../../assets/arrow-left.svg'
import CheckIcon from '../../assets/check-icon.svg'
import CrownGradient from '../../assets/crown-gradient.png'
import DividerGradient from '../../assets/divider-gradient.svg'
import RadialBackground from '../../assets/radial-background.svg'
import { DS } from '../../design-system'
import { useStore } from '../../models'
import FAQSection from './components/FAQSection'
import MembershipCard, { membershipType } from './components/MembershipCard'
//import { getAvailablePurchases } from 'react-native-iap'
import { MembershipTypes } from '../../models/api-store'
import { extendLogger, LoggerContexts } from '../../utils/logger'

const logger = extendLogger(LoggerContexts.JoinPremiumScreen)

const JoinPremiumScreen = observer(() => {
  const router = useRouter()
  const isFocused = useIsFocused()
  const {
    memberships: { membershipTypes, getMembershipType },
    analytics: { setEvent },
    api: { mutateRestorePurchase }
  } = useStore()
  const [loading, setLoading] = useState(false)
  const isAppleDevice = Platform.OS === 'ios'
  const scrollViewRef = useRef<ScrollView>(null)
  const sortedMembershipTypes = membershipTypes.slice().sort((a, b) => (a.price || 0) - (b.price || 0))

  const onBackPress = () => {
    router.navigate('/(root)/(tabs)')
  }

  const trackEvent = (membership: membershipType) => {
    setEvent('Membership Tier Tapped', {
      product: `${membership.type} Membership`
    })
  }

  const handleRestorePurchase = async () => {
    // try {
    //   setLoading(true)
    //   logger.debug('Trying to restore purchase...')
    //   const availablePurchases = await getAvailablePurchases()
    //   const tiers = Object.values(MembershipTypes).map((level) => level.toLowerCase())
    //   logger.debug('Tiers', tiers)
    //   const member = availablePurchases.find((x) => tiers.includes(x.productId.toLowerCase()))
    //   if (member?.transactionDate) {
    //     const currentTier = member.productId.toLowerCase()
    //     logger.debug('Restoring purchase for tier', currentTier)
    //     mutateRestorePurchase({
    //       startOfPeriod: member.transactionDate,
    //       tier: currentTier as MembershipTypes
    //     })
    //     navigation.navigate(TabRoutes.homeScreen, { screen: TabRoutes.homeScreen })
    //   }
    //   logger.debug('Purchase restored')
    // } catch (error) {
    //   logger.error('Error in handleRestorePurchase', error)
    //   console.log('Error in handleRestorePurchase', error)
    // } finally {
    //   setLoading(false)
    // }
  }

  useFocusEffect(
    useCallback(() => {
      getMembershipType()
    }, [getMembershipType])
  )

  useFocusEffect(() => {
    scrollViewRef.current?.scrollTo({ y: 0, animated: true })
  })

  const backAction = useCallback(() => {
    router.navigate('/(root)/(tabs)/home')
    return true
  }, [])

  useEffect(() => {
    // REVIEW: wht this type of navigation only for android?
    const backHandler = BackHandler.addEventListener('hardwareBackPress', backAction)
    return () => backHandler.remove()
  }, [])

  return (
    <Animated.View
      style={{
        backgroundColor: DS.palette['Brand/Cream'],
        flex: 1
      }}
      entering={FadeIn}
    >
      {isFocused ? <StatusBar style={'light' as StatusBarStyle} /> : null}
      <ScrollView ref={scrollViewRef}>
        <DS.Frame style={styles.container}>
          <DS.Frame style={styles.backButtonContainer}>
            <TouchableOpacity style={styles.backButton} onPress={onBackPress}>
              <ArrowLeft />
            </TouchableOpacity>
          </DS.Frame>

          <RadialBackground />
          <DS.Frame style={styles.heroContainer}>
            <Image height={51} width={66} source={CrownGradient} resizeMode="contain" />
            <DS.Text variant="Heading 1/Medium" color="Global/White">
              Join Premium
            </DS.Text>
            <DS.Text style={styles.pageSubTitle} variant="Body Medium/Regular" color="Global/White">
              Unlock premium access to our full suite of exclusive features.
            </DS.Text>
          </DS.Frame>
        </DS.Frame>

        <DS.Frame style={styles.sectionContainer}>
          <DS.Frame style={styles.sectionTitleContainer}>
            <DS.Text variant="Heading 5/Medium" style={{ color: 'black' }}>
              Compare memberships
            </DS.Text>
            <DividerGradient />
          </DS.Frame>

          <DS.Frame style={styles.sectionSubtitle}>
            <CheckIcon fill={DS.palette['Brand/Purple']} />
            <DS.Text variant="Caption/Regular">You’re currently using our free tier</DS.Text>
          </DS.Frame>
          {sortedMembershipTypes.map((membership) => (
            <MembershipCard
              key={membership.stripePriceId}
              membership={membership}
              onSelect={() => trackEvent(membership)}
            />
          ))}
          {isAppleDevice ? (
            <DS.Button
              type="On Light"
              textAlign="center"
              textColor="Global/Black"
              onPress={handleRestorePurchase}
              variant="Small/Secondary"
              rightIcon={null}
              style={styles.restoreButton}
              centerContent
              leftIcon={<DS.Icon name="Plus--enclosed" size={20} color="Global/Black" />}
              disabled={loading}
            >
              Restore Purchase
            </DS.Button>
          ) : null}
          <FAQSection />
        </DS.Frame>
      </ScrollView>
    </Animated.View>
  )
})

const styles = StyleSheet.create({
  container: {
    backgroundColor: DS.palette['Global/Black'],
    alignItems: 'center',
    paddingBottom: 24,
    position: 'relative'
  },
  heroContainer: {
    alignItems: 'center',
    transform: [{ translateY: -18 }],
    gap: 10
  },
  backButtonContainer: {
    position: 'absolute',
    top: 30,
    left: 0,
    padding: 24
  },
  backButton: {
    width: 40,
    height: 40
  },
  pageSubTitle: {
    paddingHorizontal: 40,
    textAlign: 'center'
  },
  sectionContainer: {
    backgroundColor: DS.palette['Brand/Cream'],
    borderTopStartRadius: 24,
    borderTopEndRadius: 24,
    padding: 24,
    transform: [{ translateY: -24 }]
  },
  sectionTitleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8
  },
  sectionSubtitle: {
    backgroundColor: `${DS.palette['Global/White']}80`,
    padding: 8,
    borderRadius: 12,
    flexDirection: 'row',
    gap: 8,
    marginTop: 12
  },
  restoreButton: { justifyContent: 'center', marginTop: 12 }
})

export default JoinPremiumScreen
